import React, { useMemo } from 'react';
import { LINE_COLORS, valueAt, initialsFor } from './PlayerVoteProgressionLineGraph';

const LAST_ROUND = 24;
const ROW_H = 42; // row slot height incl. gap

// Live standings companion to the progression chart. Rows are absolutely
// positioned by rank and slide to their new slot as the simulated round
// advances, with vote totals counting up in real time.
const VoteProgressionLeaderboard = ({
  series = [],
  progress = LAST_ROUND,
  runActive = false,
  hovered = null,
  onHover = () => {},
}) => {
  const standings = useMemo(() => {
    return series
      .map((s, seriesIndex) => ({
        ...s,
        seriesIndex,
        current: valueAt(s.values, Math.min(LAST_ROUND, progress)),
      }))
      .sort((a, b) => {
        if (b.current !== a.current) return b.current - a.current;
        return a.seriesIndex - b.seriesIndex;
      });
  }, [series, progress]);

  const round = Math.min(LAST_ROUND, Math.floor(progress));
  // Scrubbing while idle should still read as a specific round, not FINAL.
  const showRound = runActive || progress < LAST_ROUND;

  if (!series.length) return null;

  return (
    <div className="bg-slate-950/60 rounded-xl border border-slate-800 flex flex-col min-h-0 h-full">
      {/* Header */}
      <div className="px-3 py-2 border-b border-slate-800/70 flex items-center justify-between gap-2 flex-shrink-0">
        <span className="text-[10px] font-mono uppercase tracking-[0.14em] text-slate-500 whitespace-nowrap">
          Leaderboard
        </span>
        <span
          className={`text-[10px] font-mono font-semibold tabular-nums px-1.5 py-0.5 rounded border whitespace-nowrap ${
            showRound
              ? 'text-emerald-300 bg-emerald-500/10 border-emerald-500/30'
              : 'text-slate-500 border-slate-700/60'
          }`}
        >
          {showRound ? `RD ${round}` : 'FINAL'}
        </span>
      </div>

      {/* Rows */}
      {/* Mobile grows to fit all rows; desktop scrolls within the fixed column */}
      <div className="md:flex-1 md:min-h-0 md:overflow-y-auto styled-scrollbar p-2" onMouseLeave={() => onHover(null)}>
        <div className="relative" style={{ height: standings.length * ROW_H }}>
          {standings.map((p, rank) => {
            const color = LINE_COLORS[p.seriesIndex % LINE_COLORS.length];
            const isLeader = rank === 0;
            const isHovered = hovered === p.seriesIndex;
            const dim = hovered != null && !isHovered;
            return (
              <div
                key={p.name}
                onMouseEnter={() => onHover(p.seriesIndex)}
                className={`absolute left-0 right-0 flex items-center gap-2 px-2 rounded-lg border ${
                  isLeader
                    ? 'bg-emerald-500/10 border-emerald-500/20'
                    : isHovered
                    ? 'bg-slate-800/70 border-slate-600/60'
                    : 'bg-slate-900/40 border-slate-800/60'
                }`}
                style={{
                  height: ROW_H - 6,
                  transform: `translateY(${rank * ROW_H}px)`,
                  transition: 'transform 0.5s cubic-bezier(0.22, 1, 0.36, 1), opacity 0.15s',
                  opacity: dim ? 0.45 : 1,
                }}
              >
                <span
                  className={`w-5 text-center text-[10px] font-mono font-bold tabular-nums flex-shrink-0 ${
                    isLeader ? 'text-emerald-300' : rank < 3 ? 'text-slate-200' : 'text-slate-500'
                  }`}
                >
                  {rank + 1}
                </span>
                {p.image ? (
                  <img
                    src={p.image}
                    alt={p.name}
                    className="w-6 h-6 rounded-full object-cover flex-shrink-0 ring-1 ring-slate-700/60"
                  />
                ) : (
                  <span className="w-6 h-6 rounded-full bg-slate-800 ring-1 ring-slate-700/60 flex items-center justify-center flex-shrink-0 text-[7px] font-bold text-slate-300">
                    {initialsFor(p.name)}
                  </span>
                )}
                <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: color }} />
                <div className="flex-1 min-w-0">
                  <div className="text-[11px] font-semibold text-slate-200 truncate leading-tight">{p.name}</div>
                  <div className="text-[9px] font-mono uppercase tracking-[0.1em] text-slate-500 leading-tight">
                    {p.team}
                  </div>
                </div>
                <span
                  className={`text-xs font-mono font-bold tabular-nums flex-shrink-0 ${
                    isLeader ? 'text-emerald-300' : 'text-slate-300'
                  }`}
                >
                  {p.current.toFixed(2)}
                </span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default VoteProgressionLeaderboard;
